<template>
  <div class="container mx-auto p-4 space-y-8">
    <div class="flex justify-between items-center">
      <h1 class="text-3xl font-bold text-gray-800">
        <i class="fas fa-home mr-2 text-primary"></i>
        欢迎回来
      </h1>
      <button 
        @click="refreshData"
        class="btn-text p-2 rounded-full hover:bg-gray-100"
        :disabled="isLoading"
      >
        <i class="fas fa-sync-alt" :class="{ 'animate-spin': isLoading }"></i>
      </button>
    </div>

    <!-- 快捷操作 -->
    <div class="flex gap-4">
      <button 
        @click="startReview" 
        class="btn-primary px-6 py-2 rounded-lg"
        :disabled="!hasNotesToReview"
      >
        <i class="fas fa-play mr-2"></i>
        开始复习
      </button>
      <router-link 
        to="/notes/new"
        class="btn-secondary px-6 py-2 rounded-lg"
      >
        <i class="fas fa-plus mr-2"></i>
        添加笔记
      </router-link>
    </div>

    <!-- 复习概览 -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">

            <!-- 全部笔记 -->
            <div class="card p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-semibold text-gray-800">
            <i class="fas fa-book mr-2 text-primary"></i>
            全部笔记
          </h2>
          <span class="text-sm text-gray-500">共 {{ totalNotes }} 条</span>
        </div>
        <div class="space-y-4">
          <div class="flex justify-between items-center">
            <span class="text-gray-600">本周新增</span>
            <span class="text-2xl font-bold text-primary">{{ weeklyNewNotes }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-gray-600">本月新增</span>
            <span class="text-2xl font-bold text-secondary">{{ monthlyNewNotes }}</span>
          </div>
          <router-link 
            to="/notes" 
            class="btn-text w-full text-center mt-4 py-2 hover:bg-gray-50 rounded-lg"
          >
            查看全部笔记
            <i class="fas fa-arrow-right ml-2"></i>
          </router-link>
        </div>
      </div>
      
      <!-- 今日待复习 -->
      <div class="card p-6 bg-white rounded-xl shadow-sm">
        <h2 class="text-xl font-semibold mb-4 text-gray-800">
          <i class="fas fa-clock mr-2 text-warning"></i>
          今日待复习
        </h2>
        
        <div class="space-y-4">
          <!-- 私人笔记 -->
          <div v-if="privateNotesCount > 0 || ankiCardsCount > 0" class="space-y-3">
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="flex justify-between items-center mb-2">
                <span class="font-medium text-gray-700">
                  <i class="fas fa-sticky-note mr-1"></i>
                  私人笔记
                </span>
                <span class="text-lg font-bold text-primary">{{ privateNotesCount }}</span>
              </div>
              <button 
                @click="startPrivateNotesReview" 
                class="btn-primary w-full py-2 rounded-lg text-sm"
                :disabled="privateNotesCount === 0"
              >
                开始复习笔记
              </button>
            </div>
            
            <!-- Anki闪卡 -->
            <div class="bg-gray-50 p-4 rounded-lg">
              <div class="flex justify-between items-center mb-2">
                <span class="font-medium text-gray-700">
                  <i class="fas fa-cards-blank mr-1"></i>
                  Anki闪卡
                </span>
                <span class="text-lg font-bold text-secondary">{{ ankiCardsCount }}</span>
              </div>
              <button 
                @click="startAnkiReview" 
                class="btn-secondary w-full py-2 rounded-lg text-sm"
                :disabled="ankiCardsCount === 0"
              >
                开始复习闪卡
              </button>
            </div>
          </div>
          
          <div v-else class="text-center text-gray-500 py-4">
            <i class="fas fa-check-circle text-success text-xl mb-2"></i>
            <p>今日暂无待复习内容</p>
          </div>
        </div>
      </div>
      
      <!-- 复习进度 -->
      <div class="card p-6 bg-white rounded-xl shadow-sm">
        <h2 class="text-xl font-semibold mb-4 text-gray-800">
          <i class="fas fa-chart-line mr-2 text-success"></i>
          复习进度
        </h2>
        <div class="space-y-4">
          <div class="flex justify-between items-center">
            <span class="text-gray-600">今日已复习</span>
            <span class="text-2xl font-bold">{{ todayCompletedCount }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="text-gray-600">连续复习天数</span>
            <span class="text-2xl font-bold">{{ continuousReviewDays }}</span>
          </div>
        </div>
      </div>
      

    </div>

    <!-- 加载状态 -->
    <div v-if="isLoading" class="flex justify-center my-12">
      <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <!-- 无需复习提示 -->
    <div v-else-if="todayReviewsCount === 0" class="card p-8 text-center">
      <i class="fas fa-check-circle text-green-500 text-4xl mb-4"></i>
      <h3 class="text-xl font-semibold mb-2">今日无需复习</h3>
      <p class="text-gray-600 mb-4">你已完成所有今日复习任务，真棒！</p>
      <router-link to="/notes/new" class="btn-primary inline-block">
        <i class="fas fa-plus mr-2"></i>添加新笔记
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'

const store = useStore()
const router = useRouter()

// 加载状态
const isLoading = ref(true)

// 加载数据
onMounted(async () => {
  try {
    // 并行加载数据以提高性能
    await Promise.all([
      store.dispatch('reviews/fetchTodayReviewsCount'),
      store.dispatch('notes/fetchNotesStats')
    ])
  } catch (error) {
    console.error('加载数据失败:', error)
    ElMessage.error('加载数据失败，请刷新页面重试')
  } finally {
    isLoading.value = false
  }
})

// 添加刷新功能
const refreshData = async () => {
  isLoading.value = true
  try {
    await Promise.all([
      store.dispatch('reviews/fetchTodayReviewsCount'),
      store.dispatch('notes/fetchNotesStats')
    ])
    ElMessage.success('数据已更新')
  } catch (error) {
    console.error('刷新数据失败:', error)
    ElMessage.error('刷新数据失败')
  } finally {
    isLoading.value = false
  }
}

// 私人笔记待复习数量
const privateNotesCount = computed(() => {
  return store.state.reviews.privateNotesCount || 0
})

// Anki闪卡待复习数量
const ankiCardsCount = computed(() => {
  return store.state.reviews.ankiCardsCount || 0
})

// 总待复习数量（私人笔记 + Anki闪卡）
const todayReviewsCount = computed(() => {
  return privateNotesCount.value + ankiCardsCount.value
})

// 是否有待复习内容
const hasNotesToReview = computed(() => todayReviewsCount.value > 0)

// 开始私人笔记复习
const startPrivateNotesReview = () => {
  if (privateNotesCount.value > 0) {
    router.push('/review-today')
  }
}

// 开始Anki闪卡复习
const startAnkiReview = () => {
  router.push('/anki-review')
}

// 今日已复习数量
const todayCompletedCount = computed(() => {
  return store.state.reviews.completedCount || 0
})

// 连续复习天数
const continuousReviewDays = computed(() => {
  return store.state.reviews.continuousReviewDays || 0
})

// 开始复习
const startReview = () => {
  router.push('/review-today')
}

// 笔记统计 - 添加默认值和错误处理
const totalNotes = computed(() => {
  const total = store.state.notes.summary?.total
  return typeof total === 'number' ? total : 0
})

const weeklyNewNotes = computed(() => {
  const weeklyNew = store.state.notes.summary?.weeklyNew
  return typeof weeklyNew === 'number' ? weeklyNew : 0
})

const monthlyNewNotes = computed(() => {
  const monthlyNew = store.state.notes.summary?.monthlyNew
  return typeof monthlyNew === 'number' ? monthlyNew : 0
})
</script>